import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { ImageDto, ImagesBrokerService } from "@biorec/common";
import { MessageService } from "primeng/api";
import { ImageGroupSubtype } from "../../../domain";


@Component({
	selector: 'br-image-upload',
	standalone: true,
	templateUrl: './image-upload.component.html',
})
export class ImageUploadComponent implements OnInit, OnDestroy {


	@ViewChild('upload') upload: ElementRef<HTMLInputElement>;
	//allowed max size in KB
	private maxUploadSize = 4096;
	//allowed max image size in px width/height
	private maxSize: number;

	private subscriptions = new Subscription();
	protected thumbnailImages: { [key: string]: ImageDto[] } = {};
	private subType: ImageGroupSubtype = ImageGroupSubtype.Unique;

	constructor(
		private brokerSvc: ImagesBrokerService,
		private msgSvc: MessageService,
	) {

	}

	ngOnInit(): void {
		this.subscriptions.add(
			this.brokerSvc.onActivate.subscribe(payload => {
				this.subType = payload?.subType as ImageGroupSubtype || ImageGroupSubtype.Unique;
				this.maxSize = payload?.maxSize || null;

				if (this.brokerSvc.isNativeImage(this.subType)) {
					// trigger display of upload dialog
					this.showUpload();
				}
				// set up file upload cancel in native mode
				window.addEventListener('focus', this.handleUploadCancel);
			})
		)
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	protected showUpload() {
		this.upload.nativeElement.click();
	}


	private currTimeout: any;
	/**
		handle file upload cancel in native mode
		https://stackoverflow.com/a/74829964
	 */
	protected handleUploadCancel = (event?: Event) => {
		let hasFiles = !!this.upload.nativeElement?.files?.length;
		clearTimeout(this.currTimeout);
		this.currTimeout = setTimeout(() => {
			// cleanup
			window.removeEventListener('focus', this.handleUploadCancel);
			// emit deactivate
			if (!hasFiles) {
				this.brokerSvc.emitDeactivate(null);
			}
		}, 300);
	};

	public handleFile(event: Event) {
		if (this.upload.nativeElement.files?.[0]) {
			let file = this.upload.nativeElement.files[0];

			let err = false;
			if (!file.type.match('image\/')) {
				this.msgSvc.add({
					severity: 'error',
					summary: $localize`Error`,
					detail: $localize`Invalid file type!`
				});
				err = true;
			}
			if (this.maxUploadSize && Math.floor(file.size / 1024) > this.maxUploadSize) {
				this.msgSvc.add({
					severity: 'error',
					summary: $localize`Error`,
					detail: $localize`File too large (max. ${this.maxUploadSize}:max_upload_size: KB)!`,
				});
				err = true;
			}
			if (err) {
				this.upload.nativeElement.value = '';
				return;
			}
			let reader = new FileReader();
			reader.addEventListener('load', (event: Event) => {
				const img = new Image();
				img.src = (<any>event.target).result;
				this.resizeImage(img);

				let payload: ImageDto = {
					name: file.name.replace(/\.\w+$/, ''),
					fileName: file.name,
					full: img.src
				};
				this.brokerSvc.emitNativeImage(payload);

				// file upload dialog is opened directly, file should be
				// processed into dto and emitted without upload
				this.brokerSvc.emitDeactivate(payload as any);

				this.upload.nativeElement.value = '';
			});
			reader.readAsDataURL(file);
		}
	}

	private resizeImage(img: HTMLImageElement) {
		if (!this.maxSize) {
			return;
		}
		const bigger = Math.max(img.width, img.height);
		if (bigger > this.maxSize) {
			const canvas = document.createElement('canvas');
			if (img.width > img.height) {
				canvas.width = this.maxSize;
				canvas.height = img.height * this.maxSize / img.width;
			} else {
				canvas.height = this.maxSize;
				canvas.width = img.width * this.maxSize / img.height;
			}
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			img.src = ctx.canvas.toDataURL('image/png', 80);
		}
	}

}
